/**
 * The age groups a campaign can target, and reading an AI audience onto them.
 *
 * The form offers fixed groups, but the AI wrote its own bands — "18-34",
 * "25 to 45", "over 50" — and the form took the string as it came, so the
 * select held a value it does not have (client sheet row 273). Anything the AI
 * says about age is mapped onto the groups it overlaps; text with no age in it
 * gives null so the caller can leave the field alone.
 */

export const AGE_GROUPS = ['18-25', '26-35', '36-45', '46-55', '56-65', '65+'] as const;

const BANDS: Array<[string, number, number]> = [
  ['18-25', 18, 25], ['26-35', 26, 35], ['36-45', 36, 45],
  ['46-55', 46, 55], ['56-65', 56, 65], ['65+', 65, 120],
];

// "all genders" is not about age, so a bare "all" only counts on its own.
const ALL_AGES = /\ball\s+ages?\b|^\s*all\s*$|\bany\s+age\b|جميع\s*الأعمار|كل\s*الأعمار|جميع\s*الاعمار|كل\s*الاعمار/i;

/** The groups an age span covers. A band touched by a single year is left out. */
function groupsFor(from: number, to: number): string[] {
  return BANDS
    .filter(([, lo, hi]) => {
      const overlap = Math.min(hi, to) - Math.max(lo, from) + 1;
      const inside = from >= lo && to <= hi;
      return inside || overlap >= 2;
    })
    .map(([name]) => name);
}

/**
 * The form's age groups for whatever the AI wrote: an array of groups, or text
 * such as "26-35, 36-45, all genders", "18 to 34", "over 50" or "all ages".
 * Null when it says nothing about age.
 */
export function ageGroupsFrom(value: unknown): string[] | null {
  const parts = Array.isArray(value) ? value.map(String) : [String(value ?? '')];
  const text = parts.join(', ')
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .toLowerCase();
  if (!text.trim()) return null;

  const spans: Array<[number, number]> = [];
  const age = (n: string) => parseInt(n, 10);
  for (const m of text.matchAll(/\b(\d{2})\s*(?:-|–|—|to|until|إلى|الى|حتى)\s*(\d{2})\b/g)) {
    const a = age(m[1]), b = age(m[2]);
    if (a >= 13 && b >= a && b <= 120) spans.push([a, b]);
  }
  for (const m of text.matchAll(/\b(\d{2})\s*\+|(?:over|above|older\s+than|فوق|أكبر\s*من|اكبر\s*من)\s*(\d{2})\b/g)) {
    const a = age(m[1] ?? m[2]);
    spans.push([m[1] ? a : a + 1, 120]);
  }
  for (const m of text.matchAll(/(?:under|below|younger\s+than|أقل\s*من|اقل\s*من|تحت)\s*(\d{2})\b/g)) {
    spans.push([18, age(m[1]) - 1]);
  }

  if (spans.length) {
    const found = new Set(spans.flatMap(([a, b]) => groupsFor(a, b)));
    const groups = BANDS.map(([name]) => name).filter((g) => found.has(g));
    return groups.length ? groups : null;
  }
  return ALL_AGES.test(text) ? ['All'] : null;
}
